import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderBlocks = (blocks) => {
  const out = [];
  let list = [];
  const flush = () => {
    if (!list.length) return;
    out.push(
      <ul key={`list-${out.length}`} className="my-7 space-y-3">
        {list.map((text, j) => (
          <li key={j} className="flex gap-3 text-base text-zinc-600 leading-relaxed">
            <span className="w-1.5 h-1.5 bg-cyan-500 shrink-0 mt-2.5" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    );
    list = [];
  };
  blocks.forEach((b, i) => {
    if (b.type === "list_item") {
      list.push(b.text);
      return;
    }
    flush();
    if (b.type === "heading") {
      out.push(
        <h2 key={i} className="font-display tracking-tight text-2xl sm:text-3xl mt-12 mb-1">
          {b.text}
        </h2>
      );
    } else if (b.type === "quote") {
      out.push(
        <blockquote key={i} className="border-l-2 border-cyan-400 pl-6 my-8 text-lg text-zinc-700 italic leading-relaxed">
          {b.text}
        </blockquote>
      );
    } else if (b.type === "table") {
      out.push(
        <div key={i} className="my-9 overflow-x-auto rounded-xl border border-zinc-200" data-testid={`article-table-${i}`}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-black text-white">
                {b.header.map((h, j) => (
                  <th key={j} className="text-left font-semibold px-5 py-3.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, j) => (
                <tr key={j} className={j % 2 ? "bg-zinc-50" : "bg-white"}>
                  {row.map((cell, k) => (
                    <td key={k} className="px-5 py-3.5 text-zinc-600 border-t border-zinc-100 align-top leading-relaxed">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      out.push(
        <p key={i} className={`leading-relaxed my-6 ${i === 0 ? "text-lg sm:text-xl text-zinc-800" : "text-base text-zinc-600"}`}>
          {b.text}
        </p>
      );
    }
  });
  flush();
  return out;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 min-h-screen" data-testid="blog-post-not-found">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-display font-extrabold uppercase text-4xl">Article not found</h1>
          <Link to="/blog" className="inline-flex items-center gap-2 mt-8 text-cyan-400 font-mono-accent text-xs uppercase tracking-[0.25em]">
            <ArrowLeft className="w-4 h-4" /> Back to journal
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="pt-40 pb-32 min-h-screen" data-testid="blog-post-loading">
        <p className="text-center font-mono-accent text-xs uppercase tracking-[0.3em] text-zinc-500">
          Loading…
        </p>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="pt-20 bg-black">
      <article className="rounded-t-[2.5rem] py-24 lg:py-32 bg-white text-black">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <MaskedLine delay={0.1}>
            <Link
              to="/blog"
              data-testid="blog-post-back-link"
              className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-cyan-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> The Journal
            </Link>
          </MaskedLine>
          <div className="flex items-center gap-4 font-mono-accent text-[10px] uppercase tracking-[0.25em] mt-10">
            <span className="text-cyan-600">{post.category}</span>
            <span className="text-zinc-500">
              {new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="text-zinc-500">{post.read_time}</span>
          </div>
          <MaskedLine delay={0.2}>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6">
              {post.title}
            </h1>
          </MaskedLine>
          <div className="w-16 h-px bg-cyan-400 mt-10" />
          {post.image && (
            <div className="rounded-2xl overflow-hidden mt-10 aspect-[16/9]">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}
          <Reveal className="mt-10">
            {renderBlocks(post.blocks || [])}
          </Reveal>
          <div className="border-t border-zinc-200 mt-16 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Written by the Market Rounds team
            </p>
            <Link
              to="/contact"
              data-testid="blog-post-cta-link"
              className="inline-flex items-center gap-2 bg-cyan-400 text-black font-display font-bold text-xs uppercase tracking-wider px-6 py-3 hover:bg-white transition-colors"
            >
              Work with us
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
};

export default BlogPost;
